class WoodenSmileMobileToolbar extends HTMLElement {
  connectedCallback() {
    document.body.classList.add('ws-has-mobile-toolbar');
    this.addEventListener('click', this.handleClick);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.updateCartCount(event.cartData?.item_count);
      });
    }
  }

  disconnectedCallback() {
    document.body.classList.remove('ws-has-mobile-toolbar');
    this.removeEventListener('click', this.handleClick);
    if (this.unsubscribeCart) this.unsubscribeCart();
  }

  handleClick = (event) => {
    const control = event.target.closest('[data-ws-action]');
    if (!control) return;

    const action = control.dataset.wsAction;

    if (action === 'cart') {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
      event.preventDefault();
      cartDrawer.open(control);
    }
  };

  updateCartCount(itemCount) {
    if (!Number.isInteger(itemCount) || itemCount < 0) return;

    const count = this.querySelector('[data-ws-cart-count]');
    if (!count) return;

    count.textContent = itemCount > 99 ? '99+' : itemCount;
    count.hidden = itemCount === 0;
  }
}

if (!customElements.get('ws-mobile-toolbar')) {
  customElements.define('ws-mobile-toolbar', WoodenSmileMobileToolbar);
}

class WoodenSmileDiscoveryDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[role="dialog"]');
    this.input = this.querySelector('input[type="search"]');
    this.zeroState = this.querySelector('[data-ws-discovery-zero]');
    this.isOpen = false;

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.input?.addEventListener('input', this.handleInput);
    this.input?.form?.addEventListener('reset', this.handleReset);
    this.updateZeroState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.input?.removeEventListener('input', this.handleInput);
    this.input?.form?.removeEventListener('reset', this.handleReset);
    window.clearTimeout(this.focusReturnTimer);

    if (this.isOpen) this.close(false);
  }

  handleDocumentClick = (event) => {
    const trigger = event.target.closest('[data-ws-discovery-trigger]');
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-ws-discovery-close]') && this.contains(event.target)) {
      event.preventDefault();
      this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
    }
  };

  handleInput = () => {
    this.updateZeroState();
  };

  handleReset = () => {
    window.setTimeout(() => this.updateZeroState(), 0);
  };

  open(trigger) {
    if (!this.dialog || this.isOpen) return;

    window.clearTimeout(this.focusReturnTimer);
    this.isOpen = true;
    this.openedBy = trigger;
    document.querySelectorAll('[data-ws-discovery-origin]').forEach((item) => {
      item.removeAttribute('data-ws-discovery-origin');
    });
    trigger?.setAttribute('data-ws-discovery-origin', '');
    this.bodyOverflowWasHidden = document.body.classList.contains('overflow-hidden');
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden', 'ws-discovery-open');
    document.querySelectorAll('[data-ws-discovery-trigger]').forEach((item) => {
      item.setAttribute('aria-expanded', 'true');
    });

    const focusTarget = window.matchMedia('(min-width: 750px)').matches && this.input ? this.input : this.dialog;
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, focusTarget);
    } else {
      focusTarget.focus();
    }
  }

  close(restoreFocus = true) {
    if (!this.isOpen) return;

    const focusReturnTarget = this.openedBy || document.querySelector('[data-ws-discovery-origin]');
    window.clearTimeout(this.focusReturnTimer);
    focusReturnTarget?.removeAttribute('data-ws-discovery-origin');
    this.openedBy = null;
    this.isOpen = false;
    document.body.classList.remove('ws-discovery-open');
    if (!this.bodyOverflowWasHidden) document.body.classList.remove('overflow-hidden');
    document.querySelectorAll('[data-ws-discovery-trigger]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    if (this.contains(document.activeElement)) document.activeElement.blur();
    const focusAfterClose = document.activeElement;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');

    if (restoreFocus) {
      this.focusReturnTimer = window.setTimeout(() => {
        if (this.isOpen || !this.isConnected || !focusReturnTarget?.isConnected ||
            document.activeElement !== focusAfterClose) return;
        if (typeof wsRestoreFocus === 'function') {
          wsRestoreFocus(focusReturnTarget);
        } else {
          focusReturnTarget.focus();
        }
      }, 100);
    }
  }

  updateZeroState() {
    if (!this.zeroState || !this.input) return;
    this.zeroState.hidden = this.input.value.trim().length > 0;
  }
}

if (!customElements.get('ws-discovery-drawer')) {
  customElements.define('ws-discovery-drawer', WoodenSmileDiscoveryDrawer);
}

(() => {
  const selector = '.ws-collections-hub img.ws-collections-card__image';
  const pending = new WeakSet();
  const retried = new WeakSet();

  const canRecover = (image) =>
    image instanceof HTMLImageElement &&
    image.isConnected &&
    image.matches(selector) &&
    image.complete &&
    image.naturalWidth === 0 &&
    image.getAttribute('src')?.trim() &&
    !retried.has(image);

  const recover = (image) => {
    if (!canRecover(image) || pending.has(image) || navigator.onLine === false) return;

    const source = image.getAttribute('src');
    pending.add(image);
    window.setTimeout(() => {
      pending.delete(image);
      if (!canRecover(image) || navigator.onLine === false || image.getAttribute('src') !== source) return;

      retried.add(image);
      image.setAttribute('src', source);
    }, 500);
  };

  const scan = (root = document) => root.querySelectorAll(selector).forEach(recover);

  document.addEventListener('error', (event) => recover(event.target), true);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  window.addEventListener('online', () => scan());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
})();
